import { API_BASE_URL } from './api';
import { cacheService } from './cacheService';

export interface BackendChat {
  chat_id: string;
  user_id: string;
  companion_id?: string;
  title?: string;
  created_at: string;
  updated_at: string;
}

export interface BackendMessage {
  message_id: string;
  chat_id: string;
  sender_type: 'user' | 'companion';
  content: string;
  timestamp: string;
}

export interface CreateChatRequestBody {
  message: string;
  companion_id?: string;
  title?: string;
}

export interface CreateChatResponseBody {
  chat: BackendChat;
  messages: BackendMessage[];
}

export interface ChatMessagesResponseBody {
  chat_id: string;
  messages: BackendMessage[];
}

export interface ChatSummaryItem {
  chat_id: string;
  companion_id?: string;
  companion_name?: string;
  title?: string;
  latest_message_id?: string;
  latest_message_content?: string;
  latest_message_sender_type?: 'user' | 'companion';
  latest_message_timestamp?: string;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export const chatApi = {
  async createChat(body: CreateChatRequestBody): Promise<CreateChatResponseBody> {
    const response = await fetch(`${API_BASE_URL}/api/chats`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to create chat');
    }

    return response.json();
  },

  async getChats(): Promise<{ chats: BackendChat[] }> {
    const response = await fetch(`${API_BASE_URL}/api/chats`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to fetch chats');
    }

    return response.json();
  },

  async getChatMessages(chatId: string, useCache: boolean = true): Promise<ChatMessagesResponseBody> {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getChatMessages(chatId);
      if (cached) {
        console.log('🚀 Using cached chat messages for:', chatId);
        return cached;
      }
    }

    const response = await fetch(`${API_BASE_URL}/api/chats/${chatId}/messages`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to fetch chat messages');
    }

    const data = await response.json();
    
    // Cache the result
    if (useCache) {
      cacheService.setChatMessages(chatId, data);
    }

    return data;
  },

  async sendMessage(chatId: string, message: string): Promise<ChatMessagesResponseBody> {
    const response = await fetch(`${API_BASE_URL}/api/chats/${chatId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to send message');
    }

    return response.json();
  },

  async sendMessageNoReply(chatId: string, message: string): Promise<ChatMessagesResponseBody> {
    const response = await fetch(`${API_BASE_URL}/api/chats/${chatId}/messages/no-reply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to send message (no-reply)');
    }

    return response.json();
  },

  async ensureChatWithCompanion(companionId: string): Promise<ChatMessagesResponseBody> {
    const response = await fetch(`${API_BASE_URL}/api/chats/by-companion/${companionId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to ensure chat with companion');
    }

    return response.json();
  },

  async getChatsSummary(useCache: boolean = true): Promise<{ chats: ChatSummaryItem[] }> {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getChatsSummary();
      if (cached) {
        console.log('🚀 Using cached chat summaries');
        return cached;
      }
    }

    const response = await fetch(`${API_BASE_URL}/api/chats/summary`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to fetch chat summaries');
    }

    const data = await response.json();
    
    // Cache the result
    if (useCache) {
      cacheService.setChatsSummary(data);
    }

    return data;
  },

  async deleteChatByCompanion(companionId: string): Promise<{ deleted_count: number; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/chats/by-companion/${companionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to delete chat');
    }

    return response.json();
  },
};


